import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  makeStyles,
  createStyles,
  Button,
  useTheme,
  Badge,
  IconButton,
  Grid,
  Tooltip,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import EditIcon from "@material-ui/icons/Edit";
import ClearIcon from "@material-ui/icons/Clear";
import FileCopyIcon from "@material-ui/icons/FileCopy";

import { CustomTheme } from "../../../../types/customTheme";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import { layouts } from "chart.js";
import zIndex from "@material-ui/core/styles/zIndex";
import _ from "lodash";
import TextfieldCommon from "../../../../components/textField/TextfieldCommon";
import { getLocalStore } from "../../../../utils/store/localStore";
import { secondaryDb } from "../../../../utils/firebase/temp";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";

export interface BoxDetailsProps {
  id: any;
  setLayout: any;
  layout: any;
  widgetData: any;
  setsOpenEditModal: any;
  setEditableWidget: any;
  setSelectedWidgets: any;
  setIsOpenDeleteConfirmationModal: any;
  configDetails: any;
  clientId: any;
  selectedWidgets: any;
}

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    badge: {
      "&:hover $iconButtons": {
        opacity: 1,
        pointerEvents: "auto",
      },
    },
    root: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.background.entity_background,
      borderRadius: 10,
      padding: "0px 20px",
      height: "100%",
      border: `1px solid ${theme.palette.background.entity_border}`,
    },
    rootBoxWarning: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.custom.yellow.light,
      borderRadius: 10,
      padding: "0px 20px",
      height: "100%",
      border: `1px solid ${theme.palette.background.entity_border}`,
    },
    rootBoxNormal: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.custom.green.light,
      borderRadius: 10,
      padding: "0px 20px",
      height: "100%",
      border: `1px solid ${theme.palette.background.entity_border}`,
    },
    iconButtons: {
      opacity: 0,
      pointerEvents: "none",
      transition: "opacity 0.2s ease",
    },
    rootBoxCritical: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.custom.red.light,
      borderRadius: 10,
      padding: "0px 20px",
      height: "100%",
      border: `1px solid ${theme.palette.background.entity_border}`,
    },
    highlightedNormal: {
      color: theme.palette.custom.green.light,
      transition: "color 1s ease", // fades back to normal
    },
    colorWhite: {
      color: "white",
    },
    highlightedCritical: {
      color: theme.palette.custom.red.light,
      transition: "color 1s ease", // fades back to normal
    },
    highlightedCriticalText: {
      color: theme.palette.custom.red.light,
    },
    highlightedWarning: {
      color: theme.palette.custom.yellow.light,
      transition: "color 1s ease", // fades back to normal
    },
    highlightedWarningText: {
      color: theme.palette.custom.yellow.light,
    },
    normal: {
      color: theme.palette.custom.orange.contrastText,
      transition: "color 1s ease",
    },
    colorBlack: {
      color: "black",
    },
  }),
);

function getAverageTimeFormatted(totalSeconds: any, count: any) {
  if (count === 0) return "0 sec";

  const avgSeconds = Math.floor(totalSeconds / count);
  const minutes = Math.floor(avgSeconds / 60);
  const seconds = avgSeconds % 60;

  if (minutes > 0 && seconds > 0) {
    return `${minutes} min ${seconds} sec`;
  } else if (minutes > 0) {
    return `${minutes} min`;
  } else {
    return `${seconds} sec`;
  }
}

const SingleWidgetBox: React.FunctionComponent<BoxDetailsProps> = ({
  id,
  setLayout,
  layout,
  widgetData,
  setsOpenEditModal,
  setEditableWidget,
  setSelectedWidgets,
  setIsOpenDeleteConfirmationModal,
  configDetails,
  clientId,
  selectedWidgets,
}) => {
  const classes = useStyles();
  const [highlighted, setHighlighted] = useState(false);
  const [criticalType, setCriticalType] = useState("");
  const [warningType, setWarningType] = useState("");
  const [prevValue, setPrevValue] = useState<any>({});
  const [error, setError] = useState("");
  const [locationMetricsNode, setLocationMetricsNode] = useState<any>({});
  const [stationMetricsNode, setStationMetricsNode] = useState<any>({});
  const [value, setValue] = useState<any>({});

  const previousValueRef = useRef<any>(null);
  const isFirstRender = useRef(true);

  const getValueByPath = (obj: any, path: string[]) => {
    return path.reduce((acc, key) => acc && acc[key], obj);
  };
  useEffect(() => {
    if (
      !clientId ||
      !widgetData ||
      (widgetData?.path[0] !== "locationAverageTime" &&
        widgetData?.path[0] !== "preparationAverageTime" &&
        widgetData?.path[0] !== "deliveryAverageTime" &&
        widgetData?.path[0] !== "loadAverageTime")
    ) {
      return; // 🔥 Do nothing if clientId is not ready
    }
    // Fetch data for locationMetrics
    const locationMetricsRef = secondaryDb.ref(
      `locationMetrics/${widgetData?.clientId || clientId}/${
        widgetData?.path[0]
      }`,
    );

    locationMetricsRef.on(
      "value",
      (snapshot) => {
        if (snapshot.exists()) {
          setLocationMetricsNode(snapshot.val());
        } else {
          setLocationMetricsNode({});
        }
      },
      (error: any) => {
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      },
    );

    return () => {
      locationMetricsRef.off(); // Cleanup listener for locationMetrics
    };
  }, [clientId, widgetData?.key, widgetData?.clientId]);

  useEffect(() => {
    if (
      !clientId ||
      !widgetData ||
      widgetData?.path[0] !== "stationAverageTime"
    ) {
      return; // 🔥 Do nothing if clientId is not ready
    }

    // Fetch data for stationMetrics
    const stationMetricsRef = secondaryDb.ref(
      `stationMetrics/${widgetData?.clientId || clientId}`,
    );

    stationMetricsRef.on(
      "value",
      (snapshot) => {
        if (snapshot.exists()) {
          setStationMetricsNode(snapshot.val());
        } else {
          setStationMetricsNode({});
        }
      },
      (error: any) => {
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      },
    );

    return () => {
      stationMetricsRef.off(); // Cleanup listener for stationMetrics
    };
  }, [clientId, widgetData?.key, widgetData?.clientId]);

  useEffect(() => {
    if (widgetData?.path[0] === "locationAverageTime") {
      const value = getValueByPath(
        { locationAverageTime: locationMetricsNode },
        widgetData?.path,
      );
      setValue(value);
    } else if (widgetData?.path[0] === "deliveryAverageTime") {
      const value = getValueByPath(
        { deliveryAverageTime: locationMetricsNode },
        widgetData?.path,
      );
      setValue(value);
    } else if (widgetData?.path[0] === "preparationAverageTime") {
      const value = getValueByPath(
        { preparationAverageTime: locationMetricsNode },
        widgetData?.path,
      );
      setValue(value);
    } else if (widgetData?.path[0] === "loadAverageTime") {
      const value = getValueByPath(
        { loadAverageTime: locationMetricsNode },
        widgetData?.path,
      );
      setValue(value);
    } else {
      const value = getValueByPath(
        widgetData?.path[0] === "stationAverageTime"
          ? { stationAverageTime: stationMetricsNode }
          : { locationAverageTime: locationMetricsNode },
        widgetData?.path,
      );
      setValue(value);
    }
  }, [stationMetricsNode, locationMetricsNode, widgetData?.path]);

  useEffect(() => {
    const { criticalType, warningType } = configDetails;
    setCriticalType(criticalType);
    setWarningType(warningType);
  }, [configDetails]);

  useEffect(() => {
    if (isFirstRender.current) {
      setPrevValue(value);
      isFirstRender.current = false;
    } else if (
      JSON.stringify(previousValueRef.current) !== JSON.stringify(value)
    ) {
      setHighlighted(true);

      const holdTimer = setTimeout(() => {
        const fadeBackTimer = setTimeout(() => {
          setHighlighted(false);
          setPrevValue(value);
        }, 1000); // Fade duration: 1 second

        return () => clearTimeout(fadeBackTimer);
      }, 3000); // Hold green for 3 seconds

      return () => clearTimeout(holdTimer);
    }

    previousValueRef.current = value;
  }, [value]);

  const handleDecrementAverageValue = () => {
    const cloneLayout = _.cloneDeep(layout);

    cloneLayout.forEach((data: any) => {
      if (data.i === id) {
        const currentSize = data.data?.averageFontSize ?? 28;
        data.data["averageFontSize"] = currentSize - 1;
      }
    });
    const dataList = cloneLayout
      .filter((item: any) => item.data) // only those with a data object
      .map((item: any) => item.data);
    setLayout(cloneLayout);
    setSelectedWidgets(dataList);
  };

  const handleIncrementAverageValue = () => {
    const cloneLayout = _.cloneDeep(layout);

    cloneLayout.forEach((data: any) => {
      if (data.i === id) {
        const currentSize = data.data?.averageFontSize ?? 28;
        data.data["averageFontSize"] = currentSize + 1;
      }
    });

    const dataList = cloneLayout
      .filter((item: any) => item.data) // only those with a data object
      .map((item: any) => item.data);
    setLayout(cloneLayout);
    setSelectedWidgets(dataList);
  };

  const handleDecrement = () => {
    const cloneLayout = _.cloneDeep(layout);

    cloneLayout.forEach((data: any) => {
      if (data.i === id) {
        const currentSize = data.data?.titleFontSize ?? 12;
        data.data["titleFontSize"] = currentSize - 1;
      }
    });

    const dataList = cloneLayout
      .filter((item: any) => item.data) // only those with a data object
      .map((item: any) => item.data);
    setLayout(cloneLayout);
    setSelectedWidgets(dataList);
  };

  const handleIncrement = () => {
    const cloneLayout = _.cloneDeep(layout);

    cloneLayout.forEach((data: any) => {
      if (data.i === id) {
        const currentSize = data.data?.titleFontSize ?? 12;
        data.data["titleFontSize"] = currentSize + 1;
      }
    });

    const dataList = cloneLayout
      .filter((item: any) => item.data) // only those with a data object
      .map((item: any) => item.data);
    setLayout(cloneLayout);
    setSelectedWidgets(dataList);
  };

  const handleEdit = () => {
    setsOpenEditModal(true);
    setEditableWidget(widgetData);
  };

  const handleDelete = () => {
    setEditableWidget(widgetData);
    setIsOpenDeleteConfirmationModal(true);
  };

  const prevValueRef = useRef(null);
  const [valueChanged, setValueChanged] = useState(false);

  useEffect(() => {
    if (!highlighted && prevValueRef.current !== value) {
      setValueChanged(true);

      // Reset after 1 second so it only flashes the new color briefly
    }
    if (
      (criticalType === "blink" && value?.status === "critical") ||
      (warningType === "blink" && value?.status === "warning") ||
      value?.status === "normal"
    ) {
      const fadeBackTimer = setTimeout(() => {
        setValueChanged(false);
      }, 3000); // 1s fade duration

      return () => clearTimeout(fadeBackTimer);
    }
    prevValueRef.current = value;
  }, [value, highlighted, criticalType, warningType]);

  const handleClassName = () => {
    if (valueChanged || highlighted) {
      if (value?.status === "critical" && criticalType === "box") {
        return classes.colorWhite;
      } else if (value?.status === "warning" && warningType === "box") {
        return classes.colorBlack;
      } else if (value?.status === "critical") {
        return criticalType === "text"
          ? classes.highlightedCriticalText
          : classes.highlightedCritical;
      } else if (value?.status === "warning") {
        return warningType === "text"
          ? classes.highlightedWarningText
          : classes.highlightedWarning;
      } else if (value?.status === "normal") {
        return classes.highlightedNormal;
      } else {
        return classes.normal;
      }
    }
  };

  const handleClassNameIcon = () => {
    if (valueChanged || highlighted) {
      if (value?.status === "critical" && criticalType === "box") {
        return classes.colorWhite;
      } else if (value?.status === "warning" && warningType === "box") {
        return classes.colorBlack;
      } else {
        return classes.normal;
      }
    }
  };

  const handleBoxColor = () => {
    if (valueChanged || highlighted) {
      if (value?.status === "critical" && criticalType === "box") {
        return classes.rootBoxCritical;
      } else if (value?.status === "warning" && warningType === "box") {
        return classes.rootBoxWarning;
      } else if (warningType === "box") {
        return classes.root;
      } else {
        return classes.root; // fallback
      }
    } else {
      return classes.root;
    }
  };
  const isDarkMode = getLocalStore("isDarkMode");
  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    if (isDarkMode?.toString() === "true") {
      setIsDark(true);
    } else {
      setIsDark(false);
    }
  }, []);

  function generateKey(length = 8) {
    const charset =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let key = "";
    for (let i = 0; i < length; i++) {
      key += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return key;
  }
  /**
   * Handles the copy of a widget.
   * Creates a deep copy of the widget and assigns a new unique key.
   * Optionally adds another location to the widget path.
   * Adds the new widget to the selectedWidgets and layout arrays.
   * Calculates the new position of the widget in the grid based on the occupied cells.
   * @param {any} data The widget to be copied.
   */

  const handleCopy = (data: any) => {
    const newKey = generateKey();
    const cloneWidget: any = _.cloneDeep(data);
    const cloneSelectedWidgets = _.cloneDeep(selectedWidgets);
    const cloneLayout = _.cloneDeep(layout);

    // Assign new unique key
    cloneWidget["key"] = newKey;

    // Optionally add another location to widget path
    if (!Array.isArray(cloneWidget.path)) {
      cloneWidget.path = [];
    }

    // Example: add location based on stationNode or current time
    // cloneWidget.path.push("new-location-code"); // <<< Your logic here

    cloneSelectedWidgets.push(cloneWidget);
    setSelectedWidgets(cloneSelectedWidgets);

    // Build grid occupancy map
    const occupied: { [x: number]: Set<number> } = {};
    for (let x = 0; x < 12; x++) {
      occupied[x] = new Set<number>();
    }

    cloneLayout.forEach((item: any) => {
      for (let dx = 0; dx < item.w; dx++) {
        for (let dy = 0; dy < item.h; dy++) {
          const px = item.x + dx;
          const py = item.y + dy;
          occupied[px].add(py);
        }
      }
    });
    // Find first unoccupied spot
    let targetX = 0;
    let targetY = 0;
    let found = false;
    for (let y = 0; y < 100 && !found; y++) {
      for (let x = 0; x < 12 && !found; x++) {
        if (!occupied[x].has(y)) {
          targetX = x;
          targetY = y;
          found = true;
        }
      }
    }

    // Determine size (w, h) of original widget
    let w = 3,
      h = 1;
    const original = layout.find((item: any) => item.i === data.key);
    if (original) {
      w = original.w;
      h = original.h;
    }

    // Create new layout entry
    const newLayoutItem = {
      i: newKey,
      x: targetX,
      y: targetY,
      w,
      h,
      data: cloneWidget,
    };

    cloneLayout.push(newLayoutItem);
    setLayout(cloneLayout);
  };
  const theme: CustomTheme = useTheme();
  return (
    <Badge
      badgeContent={
        <IconButton
          className={`"no-drag" ${classes.iconButtons}`}
          //  className="no-drag"
          size="small"
          onClick={handleDelete}
          style={{
            backgroundColor: theme.palette.custom.red.main,
            color: "white",
          }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      }
      className={`${classes.badge} ${handleBoxColor()}`}
      // overlap="circular"
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Grid container style={{ position: "relative" }}>
        <Grid item xs={12}>
          {getAverageTimeFormatted(
            prevValue?.["totalTime"] ? prevValue?.["totalTime"] : 0,
            prevValue?.["count"] ? prevValue?.["count"] : 0,
          ) !==
          getAverageTimeFormatted(
            value?.["totalTime"] ? value?.["totalTime"] : 0,
            value?.["count"] ? value?.["count"] : 0,
          ) ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent:
                  widgetData?.["align"] === "left"
                    ? "start"
                    : widgetData?.["align"] === "right"
                    ? "end"
                    : "center",
              }}
            >
              <Tooltip
                title={`Value Text Size: ${
                  widgetData["averageFontSize"] ?? 28
                }`}
              >
                <Button
                  disableRipple
                  disableFocusRipple
                  disableElevation
                  style={{
                    minWidth: "24px",
                    maxWidth: "24px",
                    height: "24px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  }}
                  className="no-drag"
                >
                  <RemoveIcon
                    className={handleClassNameIcon()}
                    onClick={handleDecrementAverageValue}
                  />
                </Button>
              </Tooltip>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Typography
                  variant="h4"
                  align={widgetData["align"] ? widgetData["align"] : "center"}
                  style={{
                    fontSize: `${widgetData["averageFontSize"] ?? 28}px`,
                  }}
                  className={handleClassName()}
                >
                  {value
                    ? getAverageTimeFormatted(
                        value["totalTime"] || 0,
                        value["count"] || 0,
                      )
                    : "0 sec"}
                </Typography>
              </div>
              <Tooltip
                title={`Value Text Size: ${
                  widgetData["averageFontSize"] ?? 28
                }`}
              >
                <Button
                  disableRipple
                  disableFocusRipple
                  disableElevation
                  style={{
                    minWidth: "24px",
                    maxWidth: "24px",
                    height: "24px",
                    borderRadius: "10px",
                    color: theme.palette.custom.orange.contrastText,
                    cursor: "pointer",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  }}
                  className="no-drag"
                  onClick={handleIncrementAverageValue}
                >
                  <AddIcon className={handleClassNameIcon()} />
                </Button>
              </Tooltip>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent:
                  widgetData?.["align"] === "left"
                    ? "start"
                    : widgetData?.["align"] === "right"
                    ? "end"
                    : "center",
              }}
            >
              <Tooltip
                title={`Value Text Size: ${
                  widgetData["averageFontSize"] ?? 28
                }`}
              >
                <Button
                  disableRipple
                  disableFocusRipple
                  disableElevation
                  style={{
                    minWidth: "24px",
                    maxWidth: "24px",
                    height: "24px",
                    borderRadius: "10px",
                    marginRight: "8px",
                    cursor: "pointer",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  }}
                  className="no-drag"
                  onClick={handleDecrementAverageValue}
                >
                  <RemoveIcon className={handleClassNameIcon()} />
                </Button>
              </Tooltip>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Typography
                  variant="h4"
                  align={widgetData["align"] ? widgetData["align"] : "center"}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: `${widgetData["averageFontSize"] ?? 28}px`,
                  }}
                  className={handleClassName()}
                >
                  {value
                    ? getAverageTimeFormatted(
                        value["totalTime"] || 0,
                        value["count"] || 0,
                      )
                    : "0 sec"}
                </Typography>
              </div>
              <Tooltip
                title={`Value Text Size: ${
                  widgetData["averageFontSize"] ?? 28
                }`}
              >
                <Button
                  disableRipple
                  disableFocusRipple
                  disableElevation
                  style={{
                    minWidth: "24px",
                    maxWidth: "24px",
                    height: "24px",
                    borderRadius: "10px",
                    color: theme.palette.custom.orange.contrastText,
                    marginLeft: "8px",
                    cursor: "pointer",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  }}
                  className="no-drag"
                  onClick={handleIncrementAverageValue}
                >
                  <AddIcon className={handleClassNameIcon()} />
                </Button>
              </Tooltip>
            </div>
          )}
        </Grid>
        <Grid item xs={12} style={{ marginTop: "8px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent:
                widgetData?.["align"] === "left"
                  ? "start"
                  : widgetData?.["align"] === "right"
                  ? "end"
                  : "center",
              width: "100%",
              zIndex: 0,
            }}
          >
            <Tooltip
              title={`Label Text Size: ${widgetData["titleFontSize"] ?? 12}`}
            >
              <Button
                disableRipple
                disableFocusRipple
                disableElevation
                style={{
                  marginRight: "8px",
                  cursor: "pointer",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                }}
                className="no-drag"
                onClick={handleDecrement}
              >
                <RemoveIcon className={handleClassNameIcon()} />
              </Button>
            </Tooltip>
            <Typography
              align="left"
              style={{ fontSize: `${widgetData["titleFontSize"] ?? 12}px` }}
              className={handleClassNameIcon()}
            >
              {widgetData.label}
            </Typography>

            <Tooltip
              title={`Label Text Size: ${widgetData["titleFontSize"] ?? 12}`}
            >
              <Button
                disableRipple
                disableFocusRipple
                disableElevation
                className={"no-drag"}
                onClick={handleIncrement}
                style={{
                  backgroundColor: "transparent",
                  color: theme.palette.custom.orange.contrastText,
                  cursor: "pointer",
                  boxShadow: "none",
                }}
              >
                <AddIcon className={handleClassNameIcon()} />
              </Button>
            </Tooltip>
          </div>
        </Grid>
      </Grid>

      <div
        className={classes.iconButtons}
        style={{
          display: "flex",
          position: "absolute",
          bottom: 8,
          left: 8,
          marginLeft: "8px",
          cursor: "pointer",
          gap: 4,
        }}
      >
        <ButtonCommon
          onClick={handleEdit}
          className="no-drag"
          style={{
            minWidth: "40px",
            maxWidth: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: theme.palette.custom.yellow.main,
          }}
        >
          <EditIcon
            className={`${handleClassNameIcon() ?? ""}`}
            style={{
              position: "absolute",
              bottom: 8,
              left: 8,
              zIndex: 1,
              color: "black",
            }}
            fontSize="small"
          />
        </ButtonCommon>
        <ButtonCommon
          color="blue"
          className="no-drag"
          style={{
            minWidth: "40px",
            maxWidth: "40px",
            marginLeft: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: theme.palette.custom.blue.main,
          }}
          onClick={() => handleCopy(widgetData)}
        >
          <FileCopyIcon style={{ color: "white" }} fontSize="small" />
        </ButtonCommon>
      </div>
    </Badge>
  );
};

export default SingleWidgetBox;
